/**
 * Analytics and Conversion Tracking Utilities
 * Tracks Google Ads, Facebook Pixel, and GA4 events
 */

// Google Ads Conversion Tracking
const trackConversion = (conversionLabel, value = 0) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17465554132/' + conversionLabel,
      'value': value,
      'currency': 'USD'
    });
  }
};

// Google Analytics 4 Event Tracking
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, eventParams);
  }
};

// Facebook Pixel Event Tracking
export const trackFBEvent = (eventName, eventParams = {}) => {
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', eventName, eventParams);
  }
};

// Conversion Events
export const conversions = {
  // Sign up started
  signupStarted: () => {
    trackConversion('SIGNUP_STARTED', 10);
    trackEvent('sign_up_started');
    trackFBEvent('InitiateCheckout');
  },

  // Sign up completed
  signupCompleted: (userId) => {
    trackConversion('SIGNUP_COMPLETED', 25);
    trackEvent('sign_up', { user_id: userId });
    trackFBEvent('CompleteRegistration', { value: 25, currency: 'USD' });
  },

  // First message recorded
  firstMessageRecorded: () => {
    trackConversion('FIRST_MESSAGE', 30);
    trackEvent('first_message_recorded');
    trackFBEvent('AddToCart', { value: 30, currency: 'USD' });
  },

  // Voice cloned
  voiceCloned: () => {
    trackConversion('VOICE_CLONED', 40);
    trackEvent('voice_cloned');
    trackFBEvent('CustomizeProduct');
  },

  // Subscription started (free trial)
  trialStarted: (plan) => {
    trackConversion('TRIAL_STARTED', 50);
    trackEvent('begin_checkout', { 
      items: [{ item_name: plan, price: 0 }]
    });
    trackFBEvent('StartTrial', { value: 50, currency: 'USD' });
  },

  // Paid subscription
  subscriptionPurchased: (plan, amount) => {
    trackConversion('PURCHASE', amount);
    trackEvent('purchase', {
      transaction_id: Date.now().toString(),
      value: amount,
      currency: 'USD',
      items: [{
        item_name: plan,
        price: amount,
        quantity: 1
      }]
    });
    trackFBEvent('Purchase', { 
      value: amount, 
      currency: 'USD',
      content_name: plan
    });
  },

  // Message sent
  messageSent: (recipientCount) => {
    trackEvent('message_sent', { recipient_count: recipientCount });
  },

  // Message scheduled
  messageScheduled: (messageCount) => {
    trackEvent('message_scheduled', { message_count: messageCount });
  },

  // Podcast completed
  podcastCompleted: (questionCount) => {
    trackConversion('PODCAST_COMPLETED', 35);
    trackEvent('podcast_completed', { question_count: questionCount });
  },

  // Pricing page viewed
  pricingViewed: () => {
    trackEvent('view_item_list', { item_list_name: 'pricing_plans' });
    trackFBEvent('ViewContent', { content_name: 'Pricing Page' });
  },

  // Video played
  videoPlayed: (videoName) => {
    trackEvent('video_start', { video_title: videoName });
  },

  // FAQ engagement
  faqEngaged: () => {
    trackEvent('faq_engagement');
  },

  // 7-day retention (user returns after 7 days)
  sevenDayRetention: () => {
    trackConversion('RETENTION_7D', 100);
    trackEvent('retention_7_day');
  },

  // 30-day retention
  thirtyDayRetention: () => {
    trackConversion('RETENTION_30D', 200);
    trackEvent('retention_30_day');
  },

  // Military campaign conversions
  militaryLandingViewed: () => {
    trackEvent('military_landing_viewed');
    trackFBEvent('ViewContent', { content_name: 'Military Landing Page' });
  },

  militaryCTAClicked: () => {
    trackEvent('military_cta_clicked');
    trackFBEvent('InitiateCheckout', { content_name: 'Military Plan' });
  },

  militarySignupStarted: () => {
    trackConversion('ZWyHCL-l1YQbENTpnIhB', 15);
    trackEvent('military_signup_started');
  },

  militaryTrialStarted: () => {
    trackConversion('ZWyHCL-l1YQbENTpnIhB', 50);
    trackEvent('military_trial_started', { plan: 'military' });
  },

  // Prison families campaign conversions
  prisonLandingViewed: () => {
    trackEvent('prison_landing_viewed');
    trackFBEvent('ViewContent', { content_name: 'Prison Families Landing Page' });
  },

  prisonCTAClicked: () => {
    trackEvent('prison_cta_clicked');
    trackFBEvent('InitiateCheckout', { content_name: 'Prison Plan' });
  },

  prisonSignupStarted: () => {
    trackConversion('PRISON_SIGNUP_STARTED', 15);
    trackEvent('prison_signup_started');
  },

  prisonTrialStarted: () => {
    trackConversion('PRISON_TRIAL_STARTED', 50);
    trackEvent('prison_trial_started', { plan: 'prison' });
  },

  // Nonprofit B2B conversions
  nonprofitLandingViewed: () => {
    trackEvent('nonprofit_landing_viewed');
    trackFBEvent('ViewContent', { content_name: 'Nonprofit Landing Page' });
  },

  nonprofitContactSubmitted: () => {
    trackConversion('NONPROFIT_CONTACT', 100);
    trackEvent('nonprofit_contact_submitted');
    trackFBEvent('Lead', { content_name: 'Nonprofit Contact' });
  }
};

// Page view tracking
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', 'G-XXXXXXXXX', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
  
  if (typeof window.fbq !== 'undefined') {
    window.fbq('track', 'PageView');
  }
};

// UTM parameter extraction
export const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content')
  };
};

// Store UTM params in localStorage for attribution
export const storeUTMParams = () => {
  const utmParams = getUTMParams();
  if (utmParams.utm_source) {
    localStorage.setItem('utm_params', JSON.stringify(utmParams));
    localStorage.setItem('utm_timestamp', Date.now().toString());
  }
};

// Get stored UTM params
export const getStoredUTMParams = () => {
  const stored = localStorage.getItem('utm_params');
  return stored ? JSON.parse(stored) : null;
};

export default {
  trackConversion,
  trackEvent,
  trackFBEvent,
  conversions,
  trackPageView,
  getUTMParams,
  storeUTMParams,
  getStoredUTMParams
};
